import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Heart, ShoppingBag, Menu, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { products } from '../mock/mock';

const NAV = [
  { label: 'Shop', to: '/shop' },
  { label: 'New Arrivals', to: '/#new-arrivals' },
  { label: 'Collections', to: '/#our-collections' },
  { label: 'Bubble of Andobrée', to: '/about' },
];

const Header = () => {
  const { cartCount, setCartOpen, wishlist } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled && !searchOpen;

  const filteredResults = searchQuery.trim()
    ? products.filter((p) =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 5)
    : [];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          transparent ? 'bg-transparent' : 'bg-white border-b border-neutral-200'
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          {/* Left nav */}
          <nav className="hidden md:flex items-center gap-10 flex-1">
            {NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="text-[11px] tracking-[0.18em] uppercase text-neutral-900 hover:opacity-60 transition-opacity"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Mobile menu */}
          <button
            className="md:hidden p-2 -ml-2"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Logo */}
          <Link
            to="/"
            className="absolute left-1/2 -translate-x-1/2 font-display text-[22px] tracking-[0.18em] font-medium select-none"
          >
            ANDOBRÉE
          </Link>

          {/* Right icons */}
          <div className="flex-1 flex items-center justify-end gap-5">
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="hover:opacity-60 transition-opacity"
            >
              <Search className="w-[18px] h-[18px]" strokeWidth={1.5} />
            </button>
            <Link to="/wishlist" aria-label="Wishlist" className="relative hover:opacity-60 transition-opacity">
              <Heart
                className="w-[18px] h-[18px]"
                strokeWidth={1.5}
                fill={wishlist.length > 0 ? 'currentColor' : 'none'}
              />
            </Link>
            <button
              onClick={() => setCartOpen(true)}
              aria-label="Cart"
              className="relative hover:opacity-60 transition-opacity"
            >
              <ShoppingBag className="w-[18px] h-[18px]" strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="absolute -top-1.5 -right-2 bg-neutral-900 text-white text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60] bg-white animate-fade">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between border-b border-neutral-200">
            <span className="font-display text-[22px] tracking-[0.18em] font-medium">ANDOBRÉE</span>
            <button onClick={() => setSearchOpen(false)} aria-label="Close search">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="max-w-3xl mx-auto px-6 pt-20">
            <input
              autoFocus
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="What are you looking for?"
              className="w-full text-3xl md:text-5xl font-display font-medium border-b border-neutral-300 pb-4 focus:outline-none focus:border-neutral-900 placeholder:text-neutral-300 bg-transparent"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery.trim()) {
                  navigate(`/shop?q=${encodeURIComponent(searchQuery)}`);
                  setSearchOpen(false);
                }
              }}
            />
            <p className="text-[11px] tracking-[0.18em] uppercase text-neutral-400 mt-4">
              Press enter to search
            </p>
            {filteredResults.length > 0 && (
              <div className="mt-12">
                <p className="text-[11px] tracking-[0.18em] uppercase text-neutral-400 mb-6">Suggestions</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                  {filteredResults.map((p) => (
                    <Link
                      key={p.id}
                      to={`/product/${p.id}`}
                      onClick={() => setSearchOpen(false)}
                      className="group"
                    >
                      <div className="aspect-[3/4] bg-neutral-100 overflow-hidden mb-2">
                        <img
                          src={p.image}
                          alt={p.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                      <p className="text-xs text-neutral-900">{p.name}</p>
                      <p className="text-xs text-neutral-500">${p.price}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-white md:hidden">
          <div className="px-6 h-20 flex items-center justify-between border-b border-neutral-200">
            <button onClick={() => setMobileOpen(false)} aria-label="Close menu">
              <X className="w-5 h-5" />
            </button>
            <span className="font-display text-[20px] tracking-[0.18em] font-medium">ANDOBRÉE</span>
            <span className="w-5" />
          </div>
          <nav className="flex flex-col px-6 py-10 gap-6">
            {NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="text-2xl font-display font-medium"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;
